import { defineStore } from 'pinia'
import type { ComputedRef } from 'vue'
import { computed, reactive } from 'vue'

/**
 * Add new request types here to keep track of them.
 * You can then use the store to show a loading indicator when any of these requests are active.
 * e.g. requests.Search.active
 */
// Typescript enums count as functions for code coverage, so we ignore them.
/* c8 ignore start */
export enum RequestType {
  All = 'All', // Used in the api service to track all requests and shouldn't be used directly.
  Main = 'Main',
  MasterRecords = 'MasterRecords',
  Environment = 'Environment',
  Document = 'Document',
  Emergency = 'Emergency',
  Trades = 'Trades',
  Client = 'Client',
  Appointments = 'Appointments',
  Search = 'Search',
  Contractor = 'Contractor',
}
/* c8 ignore stop */

type RequestCounts = {
  [K in RequestType]: {
    count: ComputedRef<number>
    increment: () => void
    decrement: () => void
    active: ComputedRef<boolean>
  }
}

export const useRequestsStore = defineStore('requests', () => {
  const counts = reactive<Record<string, number>>({})

  function increment(name: RequestType) {
    counts[name] = (counts[name] || 0) + 1
  }

  function decrement(name: RequestType) {
    counts[name] = (counts[name] || 0) - 1
  }

  const requests: RequestCounts = Object.values(RequestType).reduce(
    (acc, type) => ({
      ...acc,
      [type]: {
        count: computed(() => counts[type]),
        increment: () => increment(type),
        decrement: () => decrement(type),
        active: computed(() => counts[type] > 0),
      },
    }),
    {} as RequestCounts
  )

  return {
    counts,
    ...requests,
  }
})
